import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import Card from 'app/frontend/base/components/Card'
import {
  NorthernSkyRaidToolsLink,
  TimelineRemindersLink,
} from 'app/frontend/base/components/AddonLinks'
import LinkButton from '@/components/ui/link-button'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import CreateNotePreview from '../../TeamEvent/components/CreateNotePreview'
import {
  CreateNoteSource,
  getRaidlistParticipantCharacterId,
  clearRaidPlanCreateNoteState,
  loadRaidPlanCreateNoteState,
  parseRaidlistString,
  ParsedRaidlistParticipant,
  saveRaidPlanCreateNoteState,
} from './createNoteStorage'
import { useTeam } from 'app/frontend/base/config/context/team'
import { EventPlanOverridesByPlanId } from '../../TeamEvent/components/eventActivityPlanOverrides'
import { Input } from '@/components/ui/input'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { CircleHelp } from 'lucide-react'
import {
  getClass,
  getSpecialization,
} from 'app/frontend/base/services/characterValues'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import CharacterRole, {
  characterRoles,
} from 'app/frontend/types/custom/CharacterRole'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import { useCopyToClipboard } from 'app/frontend/base/hooks/useCopyToClipboard'
import { useDefaultSetups, usePageInfo } from 'app/frontend/base/config/queries'
import {
  buildDefaultSetupEntries,
  raidVisibleMembers,
} from './default_setup/defaultSetupEntries'
import { resolveEventParticipantCharacter } from '../../TeamEvent/components/eventActivityParticipants'

type RoleSummaryEntry = { name: string; className?: string }
type RoleSummary = Record<CharacterRole, RoleSummaryEntry[]> & {
  unknown: RoleSummaryEntry[]
}

type Props = {
  owner: RaidPlanOwner
  raidPlan: RaidPlan
  planListPathPrefix: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onActorPreferredCharacterChange?: (
    actorUuid: string,
    characterId: number,
  ) => void
}

const CreateNoteDialog = ({
  owner,
  raidPlan,
  planListPathPrefix,
  open,
  onOpenChange,
  onActorPreferredCharacterChange,
}: Props) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const rosterMemberships = raidVisibleMembers(team?.members ?? [])
  const isTeamScoped = owner.ownerType === 'team'
  const {
    data: { currentExpansion },
  } = usePageInfo()
  const { data: setups } = useDefaultSetups(team?.id, isTeamScoped)
  const encounterName = useMemo(
    () =>
      currentExpansion.seasons
        .flatMap((season) => season.instances)
        .flatMap((instance) => instance.encounters)
        .find((entry) => entry.id === raidPlan.encounterId)?.name ?? '',
    [currentExpansion, raidPlan.encounterId],
  )
  const enabledDefaultSetup = useMemo(
    () =>
      setups?.find(
        (entry) =>
          entry.encounterId === raidPlan.encounterId &&
          entry.difficulty === raidPlan.difficulty &&
          entry.enabled &&
          entry.id > 0,
      ),
    [raidPlan.difficulty, raidPlan.encounterId, setups],
  )
  const defaultSetupEntries = useMemo(
    () =>
      team && enabledDefaultSetup
        ? buildDefaultSetupEntries({
            team,
            members: team.members ?? [],
            activityId: raidPlan.id,
            selections: enabledDefaultSetup.selections,
          })
        : undefined,
    [enabledDefaultSetup, raidPlan.id, team],
  )
  const defaultSetupCopyValues = {
    difficulty: difficultiesLabels[raidPlan.difficulty],
    encounter: encounterName,
  }
  const [state, setState] = useState<{
    source: CreateNoteSource | null
    raidlistInput: string
    parsedParticipants: ParsedRaidlistParticipant[] | null
    planOverrides: EventPlanOverridesByPlanId
  }>(() => {
    const stored = loadRaidPlanCreateNoteState(raidPlan.id)
    if (!stored) {
      return {
        source: null,
        raidlistInput: '',
        parsedParticipants: null,
        planOverrides: {},
      }
    }
    if (stored.source === 'roster') {
      return {
        source: 'roster',
        raidlistInput: '',
        parsedParticipants: null,
        planOverrides: stored.overrides ?? {},
      }
    }
    return {
      source: stored.parsedParticipants.length > 0 ? 'raidlist' : null,
      raidlistInput: stored.raidlistRaw,
      parsedParticipants:
        stored.parsedParticipants.length > 0 ? stored.parsedParticipants : null,
      planOverrides: stored.overrides ?? {},
    }
  })
  const [parseError, setParseError] = useState<string | null>(null)
  const [isUpdateSetupPopoverOpen, setIsUpdateSetupPopoverOpen] =
    useState(false)
  const [
    stickyActorCharacterIdsByActorUuid,
    setStickyActorCharacterIdsByActorUuid,
  ] = useState<Record<string, number>>({})
  const resolvedActorIdentityMapRef = useRef<Record<string, string>>({})
  const isUserScoped = owner.ownerType === 'user'
  const { copied, copyToClipboard } = useCopyToClipboard()
  const [resolvedNote, setResolvedNote] = useState('')
  const [rosterDisplayMode, setRosterDisplayMode] = useState<
    'visual' | 'export'
  >('visual')

  const persistState = (next: typeof state) => {
    if (next.source === 'roster') {
      saveRaidPlanCreateNoteState(raidPlan.id, {
        source: 'roster',
        raidlistRaw: '',
        parsedParticipants: [],
        overrides: next.planOverrides,
      })
      return
    }
    if (!next.parsedParticipants) return
    saveRaidPlanCreateNoteState(raidPlan.id, {
      source: 'raidlist',
      raidlistRaw: next.raidlistInput,
      parsedParticipants: next.parsedParticipants,
      overrides: next.planOverrides,
    })
  }

  const setRaidlistInput = (value: string) => {
    setState((previous) => {
      const next = { ...previous, raidlistInput: value }
      persistState(next)
      return next
    })
  }

  const roleSummary = useMemo(() => {
    const empty: RoleSummary = {
      tank: [],
      heal: [],
      melee: [],
      ranged: [],
      unknown: [],
    }
    if (state.source === 'roster') {
      if (defaultSetupEntries) {
        defaultSetupEntries.forEach((entry) => {
          const selection = entry.selections[raidPlan.id]
          if (!selection?.selected) return
          const character = resolveEventParticipantCharacter(entry, raidPlan.id)
          const role = selection.role
          const summaryEntry = {
            name: character.name,
            className: character.characterClass,
          }
          if (characterRoles.includes(role as CharacterRole)) {
            empty[role as CharacterRole].push(summaryEntry)
            return
          }
          empty.unknown.push(summaryEntry)
        })
        return empty
      }

      rosterMemberships.forEach((membership) => {
        const role = membership.role
        const entry = {
          name: membership.characterReference.name,
          className: membership.characterReference.characterClass,
        }
        if (characterRoles.includes(role)) {
          empty[role].push(entry)
          return
        }
        empty.unknown.push(entry)
      })
      return empty
    }

    if (!state.parsedParticipants) return empty

    state.parsedParticipants.forEach((participant) => {
      const specialization =
        participant.specId === null
          ? undefined
          : getSpecialization(participant.specId.toString())
      const role = specialization?.default_role ?? 'unknown'
      const className = getClass(participant.classId.toString())
      const entry = {
        name: participant.name,
        className,
      }
      if (characterRoles.includes(role as CharacterRole)) {
        empty[role as CharacterRole].push(entry)
        return
      }
      empty.unknown.push(entry)
    })

    return empty
  }, [
    defaultSetupEntries,
    raidPlan.id,
    rosterMemberships,
    state.parsedParticipants,
    state.source,
  ])

  const roleSummaryKeys = useMemo(() => {
    const baseRoles = [...characterRoles] as CharacterRole[]
    return (
      roleSummary.unknown.length > 0 ? [...baseRoles, 'unknown'] : baseRoles
    ) as Array<CharacterRole | 'unknown'>
  }, [roleSummary])

  const handleSubmitRaidlist = () => {
    const parsed = parseRaidlistString(state.raidlistInput)
    if (!parsed) {
      setParseError(t('dashboard.team.plans.create-note.invalid-raidlist'))
      return
    }
    setParseError(null)
    setState((previous) => {
      const next = {
        ...previous,
        source: 'raidlist' as const,
        parsedParticipants: parsed,
        planOverrides: {},
      }
      persistState(next)
      return next
    })
    setStickyActorCharacterIdsByActorUuid({})
  }

  const handleUpdateParsedSetup = () => {
    const parsed = parseRaidlistString(state.raidlistInput)
    if (!parsed) {
      setParseError(t('dashboard.team.plans.create-note.invalid-raidlist'))
      return
    }
    setParseError(null)
    const participantIdentitySet = new Set(
      parsed.map((participant) => participant.identity),
    )
    const nextStickyActorCharacterIdsByActorUuid = Object.entries(
      resolvedActorIdentityMapRef.current,
    ).reduce<Record<string, number>>(
      (acc, [actorUuid, participantIdentity]) => {
        if (!participantIdentitySet.has(participantIdentity)) return acc
        acc[actorUuid] = getRaidlistParticipantCharacterId(participantIdentity)
        return acc
      },
      {},
    )
    setStickyActorCharacterIdsByActorUuid(
      nextStickyActorCharacterIdsByActorUuid,
    )
    setState((previous) => {
      const next = {
        ...previous,
        source: 'raidlist' as const,
        parsedParticipants: parsed,
      }
      persistState(next)
      return next
    })
    setIsUpdateSetupPopoverOpen(false)
  }

  const handleStartRosterMode = (displayMode: 'visual' | 'export') => {
    setRosterDisplayMode(displayMode)
    setState((previous) => {
      const next = {
        ...previous,
        source: 'roster' as const,
        raidlistInput: '',
        parsedParticipants: null,
        planOverrides: {},
      }
      if (displayMode === 'visual') persistState(next)
      return next
    })
    setStickyActorCharacterIdsByActorUuid({})
    resolvedActorIdentityMapRef.current = {}
    setParseError(null)
  }

  const handleBackFromPreview = () => {
    setState({
      source: null,
      raidlistInput: '',
      parsedParticipants: null,
      planOverrides: {},
    })
    setStickyActorCharacterIdsByActorUuid({})
    resolvedActorIdentityMapRef.current = {}
    setParseError(null)
    setRosterDisplayMode('visual')
  }

  const handleClearLocalState = () => {
    clearRaidPlanCreateNoteState(raidPlan.id)
    setState({
      source: null,
      raidlistInput: '',
      parsedParticipants: null,
      planOverrides: {},
    })
    setStickyActorCharacterIdsByActorUuid({})
    resolvedActorIdentityMapRef.current = {}
    setParseError(null)
    setRosterDisplayMode('visual')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='max-w-[95vw] w-[1280px] max-h-[90vh] overflow-y-auto'
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {rosterDisplayMode === 'export' && state.source === 'roster'
              ? t('dashboard.team.plans.create-note.preview-title')
              : t('dashboard.team.plans.create-note.title')}
          </DialogTitle>
        </DialogHeader>

        {rosterDisplayMode === 'export' && state.source === 'roster' ? null : (
          <p className='max-w-[50rem]'>
            Your raid plan is a blueprint. Based on your setup of the day, a
            reminder note will be generated dynamically to use with{' '}
            <TimelineRemindersLink /> or <NorthernSkyRaidToolsLink />. You can
            make manual changes to the resolved note before exporting.
          </p>
        )}

        {state.source === 'roster' || state.parsedParticipants ? (
          <div className='space-y-3'>
            {rosterDisplayMode === 'export' &&
            state.source === 'roster' ? null : (
              <div className='flex items-center justify-between gap-2'>
                <Card className='px-3 py-2 flex items-center gap-3'>
                  {roleSummaryKeys.map((role, roleIndex) => {
                    const entries = roleSummary[role]
                    const label =
                      role === 'unknown'
                        ? t('shared.unknown')
                        : t(
                            `dashboard.team.plans.assignments.entities.roles.${role}`,
                          )
                    return (
                      <div key={role} className='flex items-center gap-3'>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className='flex items-center gap-1.5 cursor-default'>
                              {role === 'unknown' ? (
                                <CircleHelp className='h-4 w-4 text-muted-foreground' />
                              ) : (
                                <CharacterRoleIcon
                                  role={role as CharacterRole}
                                  size='small'
                                />
                              )}
                              <span className='text-xs font-semibold text-muted-foreground'>
                                {entries.length}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className='space-y-1'>
                              <p className='text-xs text-muted-foreground'>
                                {label}
                              </p>
                              {entries.length ? (
                                entries.map((entry: RoleSummaryEntry) => {
                                  const classColor =
                                    entry.className &&
                                    classColours[
                                      entry.className as keyof typeof classColours
                                    ]
                                      ? classColours[
                                          entry.className as keyof typeof classColours
                                        ].text
                                      : 'text-foreground'
                                  return (
                                    <p
                                      key={`${role}-${entry.name}`}
                                      className={classColor}
                                    >
                                      {entry.name}
                                    </p>
                                  )
                                })
                              ) : (
                                <p className='text-muted-foreground'>
                                  {t('shared.none')}
                                </p>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                        {roleIndex < roleSummaryKeys.length - 1 ? (
                          <div className='h-4 w-px bg-border' />
                        ) : null}
                      </div>
                    )
                  })}
                </Card>
                <div className='flex items-center gap-2 flex-1 justify-end'>
                  {state.source === 'raidlist' ? (
                    <Popover
                      open={isUpdateSetupPopoverOpen}
                      onOpenChange={setIsUpdateSetupPopoverOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button type='button' variant='outline'>
                          {t('dashboard.team.plans.create-note.update-setup')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className='w-[min(17rem,90vw)] p-3'>
                        <div className='space-y-2'>
                          <p>Paste your exported raid setup here</p>
                          <Input
                            value={state.raidlistInput}
                            onChange={(event) =>
                              setRaidlistInput(event.target.value)
                            }
                            onFocus={(event) => event.currentTarget.select()}
                            placeholder={t(
                              'dashboard.team.plans.create-note.placeholder',
                            )}
                          />
                          <div className='flex justify-end'>
                            <Button
                              type='button'
                              onClick={handleUpdateParsedSetup}
                            >
                              {t('dashboard.team.plans.create-note.submit')}
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : null}
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleClearLocalState}
                  >
                    {t('dashboard.team.plans.create-note.clear')}
                  </Button>
                  <Button
                    type='button'
                    onClick={() => copyToClipboard(resolvedNote)}
                    disabled={resolvedNote.trim().length === 0}
                  >
                    {copied ? 'Copied!' : 'Copy note'}
                  </Button>
                </div>
              </div>
            )}
            {parseError ? (
              <p className='text-sm text-destructive-foreground'>
                {parseError}
              </p>
            ) : null}
            <CreateNotePreview
              owner={owner}
              raidPlan={raidPlan}
              planListPathPrefix={planListPathPrefix}
              planAbilityDefaults={team?.planAbilityDefaults}
              displayMode={rosterDisplayMode}
              onBack={
                rosterDisplayMode === 'export' && state.source === 'roster'
                  ? handleBackFromPreview
                  : undefined
              }
              parsedParticipants={state.parsedParticipants ?? undefined}
              rosterMemberships={
                state.source === 'roster' ? rosterMemberships : undefined
              }
              participantEntries={
                state.source === 'roster' ? defaultSetupEntries : undefined
              }
              planOverrides={state.planOverrides}
              stickyActorCharacterIdsByActorUuid={
                stickyActorCharacterIdsByActorUuid
              }
              onResolvedActorIdentityMapChange={(value) => {
                resolvedActorIdentityMapRef.current = value
              }}
              onResolvedNoteChange={setResolvedNote}
              allowUnresolvedActorOverride={state.source === 'roster'}
              onActorPreferredCharacterChange={
                state.source === 'roster'
                  ? onActorPreferredCharacterChange
                  : undefined
              }
              onPlanOverridesChange={(nextPlanOverrides) => {
                setState((previous) => {
                  const next = {
                    ...previous,
                    planOverrides: nextPlanOverrides,
                  }
                  persistState(next)
                  return next
                })
              }}
            />
            {rosterDisplayMode === 'export' &&
            state.source === 'roster' ? null : (
              <div className='flex justify-end'>
                <Button
                  type='button'
                  onClick={() => copyToClipboard(resolvedNote)}
                  disabled={resolvedNote.trim().length === 0}
                >
                  {copied ? 'Copied!' : 'Copy note'}
                </Button>
              </div>
            )}
          </div>
        ) : isUserScoped ? (
          <div className='space-y-3'>
            <p>
              You can import your setup using the{' '}
              <a
                href='https://www.curseforge.com/wow/addons/wowaudit-companion'
                target='_blank'
                rel='noopener noreferrer'
                className='text-primary hover:text-primary/90'
              >
                companion addon
              </a>
              .
            </p>
            <Input
              value={state.raidlistInput}
              onChange={(event) => setRaidlistInput(event.target.value)}
              placeholder={t('dashboard.team.plans.create-note.placeholder')}
            />
            {parseError ? (
              <p className='text-sm text-destructive-foreground'>
                {parseError}
              </p>
            ) : null}
            <div className='flex justify-end'>
              <Button type='button' onClick={handleSubmitRaidlist}>
                {t('dashboard.team.plans.create-note.submit')}
              </Button>
            </div>
          </div>
        ) : (
          <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
            <Card className='space-y-2'>
              <h4>{t('dashboard.team.plans.create-note.events-card.title')}</h4>
              <p>
                {t('dashboard.team.plans.create-note.events-card.description')}
              </p>
              <LinkButton to={`${planListPathPrefix}/events`} variant='default'>
                {t('dashboard.team.plans.create-note.events-card.action')}
              </LinkButton>
            </Card>
            <Card className='space-y-2'>
              <h4>{t('dashboard.team.plans.create-note.direct-card.title')}</h4>
              <p>
                {enabledDefaultSetup
                  ? t(
                      'dashboard.team.plans.create-note.direct-card.description-default-setup',
                      defaultSetupCopyValues,
                    )
                  : t(
                      'dashboard.team.plans.create-note.direct-card.description',
                    )}
              </p>
              <div className='flex justify-start gap-2'>
                <Button
                  type='button'
                  onClick={() => handleStartRosterMode('visual')}
                >
                  {t('dashboard.team.plans.create-note.direct-card.action')}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => handleStartRosterMode('export')}
                >
                  {t('dashboard.team.plans.create-note.direct-card.preview')}
                </Button>
              </div>
            </Card>
            <Card className='space-y-2'>
              <h4>{t('dashboard.team.plans.create-note.paste-card.title')}</h4>
              <p>
                If you are already in a group in-game, you can import your setup
                using the{' '}
                <a
                  href='https://www.curseforge.com/wow/addons/wowaudit-companion'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-primary hover:text-primary/90'
                >
                  companion addon
                </a>
                .
              </p>
              <Input
                value={state.raidlistInput}
                onChange={(event) => setRaidlistInput(event.target.value)}
                placeholder={t('dashboard.team.plans.create-note.placeholder')}
              />
              {parseError ? (
                <p className='text-sm text-destructive-foreground'>
                  {parseError}
                </p>
              ) : null}
              <div className='flex justify-end'>
                <Button type='button' onClick={handleSubmitRaidlist}>
                  {t('dashboard.team.plans.create-note.submit')}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CreateNoteDialog
